import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "@/api/admin";

export const Route = createFileRoute("/admin/sweeps")({
  component: AdminSweepsPage,
});

// "swept" is a derived state in the server query (status=received +
// swept_at IS NOT NULL). The other values map directly to the column.
const STATUSES = ["", "pending", "received", "swept", "expired"] as const;

function AdminSweepsPage() {
  const [status, setStatus] = useState<string>("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-sweeps", status],
    queryFn: () => adminApi.listSweeps({ status: status || undefined, limit: 500 }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">On-chain sweeps</h1>
        <p className="mt-1 text-ink/65">
          Deposit addresses minted per payment + their receive / sweep state. Tx signatures
          link to Solscan for spot-checking.
        </p>
      </div>

      <div className="flex gap-3">
        <select className="input w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((s) => (
            <option key={s || "all"} value={s}>
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Deposit address</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Received</th>
              <th className="px-4 py-3">Receive tx</th>
              <th className="px-4 py-3">Swept</th>
              <th className="px-4 py-3">Sweep tx</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="px-4 py-6 text-ink/55">Loading…</td></tr>
            ) : data?.sweeps.length ? (
              data.sweeps.map((s) => (
                <tr key={s.id} className="border-t border-ink/8">
                  <td className="px-4 py-3 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    {s.project_name ?? <code className="font-mono text-xs">{s.project_id.slice(0, 8)}…</code>}
                    <span className="ml-1 text-xs text-ink/45">#{s.autoincrement_id}</span>
                  </td>
                  <td className="px-4 py-3"><AddressLink address={s.public_key} /></td>
                  <td className="px-4 py-3">
                    <StatusBadge status={s.status} swept={!!s.swept_at} />
                  </td>
                  <td className="px-4 py-3">{s.received_amount ?? "—"}</td>
                  <td className="px-4 py-3"><TxLink sig={s.received_tx_sig} /></td>
                  <td className="px-4 py-3 text-xs">{s.swept_at ? new Date(s.swept_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3"><TxLink sig={s.swept_tx_sig} /></td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={8} className="px-4 py-6 text-ink/55">No sweeps.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status, swept }: { status: string; swept: boolean }) {
  // "received" but not yet swept = funds detected, awaiting sweep.
  // "received" + swept_at = fully reconciled with the vault.
  const label = status === "received" && swept ? "swept" : status;
  const cls = label === "swept"
    ? "bg-green-500/15 text-green-600"
    : label === "received"
    ? "bg-peach-500/10 text-peach-500"
    : label === "expired"
    ? "bg-ink/4 text-ink/55"
    : "bg-amber-500/15 text-amber-600";
  return <span className={`rounded px-1.5 py-0.5 text-xs ${cls}`}>{label}</span>;
}

function AddressLink({ address }: { address: string }) {
  return (
    <a
      href={`https://solscan.io/account/${address}`}
      target="_blank" rel="noreferrer"
      className="font-mono text-xs text-primary-600 hover:underline"
      title={address}
    >
      {address.slice(0, 6)}…{address.slice(-4)}
    </a>
  );
}

function TxLink({ sig }: { sig: string | null }) {
  if (!sig) return <span className="text-ink/45">—</span>;
  return (
    <a
      href={`https://solscan.io/tx/${sig}`}
      target="_blank" rel="noreferrer"
      className="font-mono text-xs text-primary-600 hover:underline"
      title={sig}
    >
      {sig.slice(0, 6)}…{sig.slice(-4)}
    </a>
  );
}
